import {Component, ViewChild, signal, inject, DestroyRef} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {StoreDefaultWorkingTimeService} from '../../../../services/http/store/store-default-working-time.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * Defines the data required by the delete working time modal.
 */
export interface DefaultWorkingTimesDeleteData {
  storeId?: string;
  startDate: string;
}

/**
 * This component handles deleting an existing working time.
 */
@Component({
  selector: 'app-default-working-times-delete',
  templateUrl: './default-working-times-delete.component.html',
  styleUrls: ['./default-working-times-delete.component.less'],
  imports: [SHARED_IMPORTS]
})
export class DefaultWorkingTimesDeleteComponent {
  private storeDefaultWorkingTimeService = inject(StoreDefaultWorkingTimeService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** A modal of working time form. */
  @ViewChild('modalComponent') modalComponent: ModalComponent;
  /** An identifier of a store. */
  readonly storeId = signal<string>(undefined);
  /** The Monday of the week, from which default working times will be deleted. */
  readonly startDate = signal<string>(undefined);

  /** `True` if a request sending to backend is in progress. */
  loading = signal(false);

  static open(modalService: NgbModal, data: DefaultWorkingTimesDeleteData): NgbModalRef {
    const modalRef = modalService.open(DefaultWorkingTimesDeleteComponent);
    modalRef.componentInstance.storeId.set(data.storeId);
    modalRef.componentInstance.startDate.set(data.startDate);
    return modalRef;
  }

  /**
   * Sends request to backend to delete store working time.
   */
  confirm(): void {
    this.loading.set(true);

    this.storeDefaultWorkingTimeService.deleteDefaultStoreWorkingTime(this.storeId(), this.startDate())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Store trading time has been successfully deleted');
                this.ruleCheckerService.check();
              },
              error: error => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}